import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { customerProjectsQueryOptions } from '@/entities/task'
import { pluralRu } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { EmptyState } from '@/shared/ui/empty-state'
import { Icon } from '@/shared/ui/icon'
import { PageHeader } from '@/shared/ui/page-header'
import { Spinner } from '@/shared/ui/spinner'

export const Route = createFileRoute('/my/projects')({
  head: () => ({ meta: [{ title: 'Мои проекты · STIT Task' }] }),
  component: MyProjectsPage,
})

// Вход в витрину: проекты компании заказчика, где ему открыта хотя бы одна доска.
// Отбор — на бэкенде (двойной гейт задача+доска); клиент здесь ничего не прячет и
// не фильтрует, иначе «открыто» значило бы разное на сервере и на экране.
function MyProjectsPage() {
  const projectsQ = useQuery(customerProjectsQueryOptions)
  const projects = projectsQ.data ?? []

  return (
    <div className="container-x px-4 py-8 sm:px-6 sm:py-10">
      <PageHeader
        title="Мои проекты"
        description="Проекты, по которым команда открыла вам работу. Внутри — доски и их этапы."
      />

      {projectsQ.isPending ? (
        <Spinner label="Загрузка проектов" />
      ) : projectsQ.isError ? (
        <div className="flex flex-col items-start gap-3">
          <p className="text-sm text-text-secondary">Не удалось загрузить проекты.</p>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              void projectsQ.refetch()
            }}
          >
            Повторить
          </Button>
        </div>
      ) : projects.length === 0 ? (
        <EmptyState
          title="Проектов пока нет"
          description="Здесь появятся ваши проекты, как только команда откроет вам доску."
        />
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {projects.map((project) => (
            <li key={project.id}>
              <Link
                to="/my/projects/$projectId"
                params={{ projectId: project.id }}
                className="group flex h-full flex-col gap-2 rounded-card bg-bg-surface p-5 ring-1 ring-border-subtle ring-inset transition-shadow hover:shadow-[0_8px_24px_rgba(0,0,0,0.35)]"
              >
                <h2 className="text-sm text-text-primary transition-colors group-hover:text-accent">
                  {project.name}
                </h2>
                {project.description && (
                  <p className="line-clamp-2 text-xs text-text-tertiary">{project.description}</p>
                )}
                <div className="mt-auto flex items-center gap-3 pt-2 text-xs text-text-tertiary">
                  <span className="inline-flex items-center gap-1.5">
                    <Icon name="kanban" size={13} />
                    <span data-numeric className="mono-num">
                      {project.boardCount}
                    </span>
                    {pluralRu(project.boardCount, ['доска', 'доски', 'досок'])}
                  </span>
                  <span aria-hidden>·</span>
                  <span className="inline-flex items-center gap-1.5">
                    <span data-numeric className="mono-num">
                      {project.taskCount}
                    </span>
                    {pluralRu(project.taskCount, ['задача', 'задачи', 'задач'])}
                  </span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
